import { sources } from '../data/sources';
import { curatedModels } from '../data/models';
import { chips, computeProviders, modelPlatforms } from '../data/stack';
import { builders } from '../data/builders';
import { regionalPlatforms } from '../data/geography';
import { apiCatalog } from '../data/apiCatalog';
import { clusterSystems, knownClusters } from '../data/clusters';

export const OPENROUTER_API = 'https://openrouter.ai/api/v1/models';
import { Source } from '../types/census';

export interface IndexedSource {
  n: number;
  id: string | null;
  name: string;
  url: string;
  publisher: string;
  kind: Source['kind'];
}

export function hostOf(url: string): string {
  if (url.startsWith('/')) return 'agentpopulation.com';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const list: IndexedSource[] = [];
const byUrl = new Map<string, IndexedSource>();
const byId = new Map<string, IndexedSource>();

function add(id: string | null, name: string, url: string, publisher: string, kind: Source['kind']) {
  const existing = byUrl.get(url);
  if (existing) {
    if (id) byId.set(id, existing);
    return;
  }
  const entry: IndexedSource = { n: list.length + 1, id, name, url, publisher, kind };
  list.push(entry);
  byUrl.set(url, entry);
  if (id) byId.set(id, entry);
}

sources.forEach((s) => add(s.id, s.name, s.url, s.publisher, s.kind));
curatedModels.forEach((m) => add(null, `${m.model} — ${m.lab} announcement`, m.sourceUrl, hostOf(m.sourceUrl), 'official'));
modelPlatforms.forEach((p) => add(null, `${p.name} — models & pricing`, p.sourceUrl, hostOf(p.sourceUrl), 'official'));
builders.forEach((b) => add(null, `${b.org} — official site`, b.sourceUrl, hostOf(b.sourceUrl), 'official'));
regionalPlatforms.forEach((p) => add(null, `${p.name} — official page`, p.url, hostOf(p.url), 'official'));
add(null, 'Hacker News search API (Algolia) — AI stories', 'https://hn.algolia.com/api', 'hn.algolia.com', 'api');
add(null, 'Hugging Face — trending models', 'https://huggingface.co/api/models?sort=trendingScore&limit=30', 'huggingface.co', 'api');
clusterSystems.forEach((s) => add(null, `${s.system} — vendor system page`, s.sourceUrl, hostOf(s.sourceUrl), 'official'));
knownClusters.forEach((k) => add(null, `${k.name} — ${k.operator}`, k.sourceUrl, hostOf(k.sourceUrl), 'publication'));
apiCatalog.forEach((a) => add(null, `${a.name} — API documentation`, a.docs, hostOf(a.docs), 'api'));
computeProviders.forEach((c) => {
  add(null, `${c.name} — GPU offering & pricing`, c.sourceUrl, hostOf(c.sourceUrl), 'official');
  if (c.scaleSourceUrl) add(null, `${c.name} — scale disclosure (filing)`, c.scaleSourceUrl, hostOf(c.scaleSourceUrl), 'official');
});
chips.forEach((c) => add(null, `${c.maker} ${c.name} — vendor specifications`, c.sourceUrl, hostOf(c.sourceUrl), 'official'));
add(null, 'OpenRouter models API', OPENROUTER_API, 'openrouter.ai', 'api');

export const sourceIndex: IndexedSource[] = list;

export function getSourceById(id: string): IndexedSource | undefined {
  return byId.get(id);
}

export function lookupByUrl(url: string): IndexedSource | undefined {
  return byUrl.get(url);
}
